import type { SystemStyleObject, Theme } from '@mui/system';
import { useFieldWarningEvaluation } from '@src/shared/form/FieldWarningEvaluationContext';
import useFormMethodsContext from '@src/shared/form/useFormMethodsContext';
import Avatar from '@src/shared/ui/Avatar';
import { IconButton } from '@src/shared/ui/Button';
import { FormHelperText } from '@src/shared/ui/form';
import { DeleteIcon, RefreshIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import { type ChangeEvent, useId } from 'react';
import {
  type Control,
  type FieldValues,
  useController,
  useWatch,
} from 'react-hook-form';

const StyleSheet = {
  root: {
    backgroundColor: 'grey.100',
    height: 50,
    p: '1px 10px 8px',
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    gap: 1.25,
  },
  avatar: {
    backgroundColor: 'grey.200',
    borderRadius: 1,
    height: 32,
    width: 32,
  },
  actionButton: {
    backgroundColor: 'common.white',
    height: 32,
    width: 32,
    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  },
};

type FormContextShape = {
  control: Control<FieldValues>;
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
};

type UserProfileImageUploadFieldProps = {
  name: string;
  label: string;
  disabled?: boolean;
  styles?: {
    image?: SystemStyleObject<Theme>;
    root?: SystemStyleObject<Theme>;
  };
};

function UserProfileImageUploadField({
  name,
  label,
  disabled = false,
  styles = {},
}: UserProfileImageUploadFieldProps) {
  const { control, setValue } = useFormMethodsContext() as FormContextShape;
  const warningEvaluation = useFieldWarningEvaluation();
  const { field, fieldState } = useController({ control, name });
  const inputId = useId();
  const helperTextId = useId();
  const previewName = `${name}Preview`;
  const imagePreview = useWatch({ control, name: previewName }) as
    | string
    | null;
  const hasError = Boolean(fieldState.error);
  const actionButtonStyles = {
    ...StyleSheet.actionButton,
    ...(hasError
      ? {
          borderColor: 'error.main',
          color: 'error.main',
          '&:hover': {
            borderColor: 'error.main',
          },
        }
      : {}),
  };

  const clearImage = () => {
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
    warningEvaluation?.changeValue(name);
    warningEvaluation?.commitValue(name);
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    setValue(name, file, { shouldDirty: true, shouldValidate: true });
    warningEvaluation?.changeValue(name);
    warningEvaluation?.commitValue(name);

    if (!file) {
      setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue(
        previewName,
        typeof reader.result === 'string' ? reader.result : null,
        { shouldDirty: true, shouldValidate: true },
      );
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={{ ...StyleSheet.root, ...styles.root }}>
      <Typography
        variant="body2"
        color={hasError ? 'error.main' : 'text.secondary'}
        sx={{ mb: 0 }}
      >
        {label}
      </Typography>
      <Box sx={StyleSheet.content}>
        {imagePreview ? (
          <Avatar
            src={imagePreview}
            sx={{ ...StyleSheet.avatar, ...styles.image }}
          />
        ) : null}
        <IconButton
          component="label"
          color="secondary"
          size="small"
          disabled={disabled}
          aria-label={`${imagePreview ? 'Replace' : 'Upload'} ${label}`}
          sx={actionButtonStyles}
        >
          {imagePreview ? (
            <RefreshIcon color="error" />
          ) : (
            <UploadIcon color="error" />
          )}
          <input
            ref={field.ref}
            id={inputId}
            hidden
            name={field.name}
            type="file"
            accept="image/*"
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? helperTextId : undefined}
            onBlur={field.onBlur}
            onChange={handleImageChange}
          />
        </IconButton>
        {imagePreview ? (
          <IconButton
            color="secondary"
            size="small"
            disabled={disabled}
            aria-label={`Delete ${label}`}
            onClick={clearImage}
            sx={actionButtonStyles}
          >
            <DeleteIcon color="error" />
          </IconButton>
        ) : null}
      </Box>
      {fieldState.error?.message && (
        <FormHelperText id={helperTextId} error sx={{ ml: 0 }}>
          {fieldState.error.message}
        </FormHelperText>
      )}
    </Box>
  );
}

export default UserProfileImageUploadField;
